import { useEnum } from "@/api/services/global/enum";
import { TCaseListOutputDto } from "@/api/services/main/case/dtos/get-case-list.dto";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { CaseTagStatus } from "@/features/case/components/CaseTagStatus";
import { TCaseStatus } from "@/features/case/constants/caseStatus";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { divider } from "@/theme/colors";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import { dayjs } from "@/utils/dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TCaseListDisplayColumnSetting } from "@/features/case/components/CaseList/case-list-filter-shared";

type TCaseListTableProps = {
  rows: TCaseListOutputDto[];
  isLoading: boolean;
  displayColumnSettings?: TCaseListDisplayColumnSetting[] | null;
};

export const CaseTable: FC<TCaseListTableProps> = ({ rows, isLoading, displayColumnSettings }) => {
  const navigate = useNavigate();
  const { data: caseStatusOptions } = useEnum("ECaseStatus");

  const truncateText = (value: string | null | undefined, maxLength: number = 15) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, maxLength);
  };

  const columns = useMemo<MRT_ColumnDef<TCaseListOutputDto>[]>(
    () => [
      {
        id: "progressStatus",
        header: "ステータス",
        accessorKey: "progressStatus",
        maxSize: 134,
        Cell: ({ row }) => {
          return (
            <CenteredCell>
              <CaseTagStatus status={row.original?.progressStatus as TCaseStatus} isAllFailed={row.original?.isAllFailed} />
            </CenteredCell>
          );
        },
        Header: <TableSorting name="case.progressStatus">ステータス</TableSorting>,
      },
      {
        id: "managementNumber",
        header: "案件管理番号",
        maxSize: 116,
        Cell: ({ row }) => truncateText(row.original?.managementNumber) ?? EMPTY_STR.TEXT,
      },
      {
        id: "name",
        header: "案件名",
        maxSize: 124,
        Cell: ({ row }) => truncateText(row.original?.name) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="case.name">案件名</TableSorting>,
      },
      {
        id: "jobName",
        header: "求人名",
        maxSize: 144,
        Cell: ({ row }) => truncateText(row.original?.job?.jobName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="job.jobName">求人名</TableSorting>,
      },
      {
        id: "companyName",
        header: "企業名",
        maxSize: 158,
        Cell: ({ row }) => truncateText(row.original?.job?.company?.companyName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="company.companyName">企業名</TableSorting>,
      },
      {
        id: "companySpecificIndustry",
        header: "分野",
        maxSize: 100,
        Cell: ({ row }) => truncateText(row.original?.job?.companySpecificIndustry?.companySpecificIndustry) ?? EMPTY_STR.TEXT,
      },
      {
        id: "workplace",
        header: "勤務地",
        maxSize: 100,
        Cell: ({ row }) => truncateText(row.original?.job?.workplace, 10) ?? EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentNumber",
        header: "募集人数",
        maxSize: 88,
        Cell: ({ row }) => (row.original?.job?.recruitmentNumber != null ? `${row.original.job.recruitmentNumber}人` : EMPTY_STR.TEXT),
      },
      {
        id: "caseMatchCount",
        header: "提案人数",
        maxSize: 88,
        Cell: ({ row }) => (row.original?.caseMatchCount != null ? `${row.original.caseMatchCount}人` : EMPTY_STR.TEXT),
      },
      {
        id: "fullName",
        header: "案件担当者",
        maxSize: 134,
        Cell: ({ row }) => truncateText(row.original?.personInCharge?.fullName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="personInCharge.fullName">案件担当者</TableSorting>,
      },
      {
        id: "createdAt",
        header: "登録日",
        maxSize: 106,
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format("YYYY/MM/DD") : EMPTY_STR.TEXT),
        Header: <TableSorting name="case.createdAt">登録日</TableSorting>,
      },
    ],
    [caseStatusOptions],
  );

  const toEditRoute = (caseId: number, jobId?: number) => {
    if (!caseId || !jobId) return;
    const route = getDynamicRoute(AppRoutes.caseEdit, {
      jobId: String(jobId),
      caseId: String(caseId),
    });
    navigate(route);
  };

  const [heightTable] = useState<string>("calc(100vh - 343px)");

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toEditRoute(row.id, row.job.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
  });

  // Apply display column visibility and ordering from settings
  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder([...visibleIds]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  return (
    <Table
      table={table}
      isLoading={isLoading}
      height={heightTable}
      minHeight={150}
      headRowHeight={37}
      bodyRowHeight={42}
      borderColor={divider.high}
    />
  );
};
